import { useState, useEffect, useCallback } from 'react';
import { promoCodesApi, PromoCode, CreatePromoCodeDto, UpdatePromoCodeDto } from '../../../api/payments';
import { toast } from 'sonner';
import { UsePromoCodesReturn } from '../types';

const initialFormData: CreatePromoCodeDto = {
  code: '',
  type: 'percent',
  value: 10,
  max_uses: null,
  valid_from: null,
  valid_until: null,
  is_active: true,
};

export function usePromoCodes(): UsePromoCodesReturn {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // На админке по умолчанию видим все, включая неактивные.
  const [includeInactive, setIncludeInactive] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState<PromoCode | null>(null);
  const [formData, setFormData] = useState<CreatePromoCodeDto>(initialFormData);

  const fetchPromoCodes = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await promoCodesApi.getAll(includeInactive);
      setPromoCodes(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch promo codes');
    } finally {
      setIsLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  const handleCreate = async () => {
    try {
      if (!formData.code.trim()) {
        toast.error('Code is required');
        return;
      }
      if (formData.type === 'percent' && (formData.value < 1 || formData.value > 100)) {
        toast.error('Percent value must be between 1 and 100');
        return;
      }
      if (formData.type === 'fixed' && formData.value < 1) {
        toast.error('Fixed value must be greater than 0');
        return;
      }
      if (formData.valid_from && formData.valid_until && new Date(formData.valid_from) > new Date(formData.valid_until)) {
        toast.error('Valid from date cannot be after valid until date');
        return;
      }

      const payload: CreatePromoCodeDto = {
        code: formData.code.trim().toUpperCase(),
        type: formData.type,
        value: formData.value,
        max_uses: formData.max_uses || null,
        valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        is_active: formData.is_active,
      };

      await promoCodesApi.create(payload);
      toast.success('Promo code created successfully');
      setIsCreateDialogOpen(false);
      setFormData(initialFormData);
      fetchPromoCodes();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create promo code');
    }
  };

  const handleEdit = (promoCode: PromoCode) => {
    setEditingPromoCode(promoCode);
    setFormData({
      code: promoCode.code,
      type: promoCode.type,
      value: promoCode.value,
      max_uses: promoCode.max_uses,
      valid_from: promoCode.valid_from || null,
      valid_until: promoCode.valid_until || null,
      is_active: promoCode.is_active,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingPromoCode) return;
    try {
      if (formData.type === 'percent' && (formData.value < 1 || formData.value > 100)) {
        toast.error('Percent value must be between 1 and 100');
        return;
      }
      if (formData.type === 'fixed' && formData.value < 1) {
        toast.error('Fixed value must be greater than 0');
        return;
      }
      if (formData.valid_from && formData.valid_until && new Date(formData.valid_from) > new Date(formData.valid_until)) {
        toast.error('Valid from date cannot be after valid until date');
        return;
      }

      const payload: UpdatePromoCodeDto = {
        code: formData.code.trim().toUpperCase(),
        type: formData.type,
        value: formData.value,
        max_uses: formData.max_uses || null,
        valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        is_active: formData.is_active,
      };

      await promoCodesApi.update(editingPromoCode.id, payload);
      toast.success('Promo code updated successfully');
      setIsEditDialogOpen(false);
      setEditingPromoCode(null);
      fetchPromoCodes();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update promo code');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this promo code?')) return;
    try {
      await promoCodesApi.delete(id);
      toast.success('Promo code deleted successfully');
      fetchPromoCodes();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete promo code');
    }
  };

  // Optimistic toggle is_active: сразу обновляем UI, при ошибке откатываем.
  const handleToggleActive = async (promoCode: PromoCode) => {
    const next = !promoCode.is_active;
    setPromoCodes((prev) =>
      prev.map((p) => (p.id === promoCode.id ? { ...p, is_active: next } : p)),
    );
    try {
      await promoCodesApi.update(promoCode.id, { is_active: next });
      toast.success(next ? 'Promo code activated' : 'Promo code deactivated');
    } catch (error: any) {
      setPromoCodes((prev) =>
        prev.map((p) =>
          p.id === promoCode.id ? { ...p, is_active: promoCode.is_active } : p,
        ),
      );
      toast.error(error.response?.data?.message || 'Failed to update promo code status');
    }
  };

  return {
    promoCodes,
    isLoading,
    includeInactive,
    isCreateDialogOpen,
    isEditDialogOpen,
    editingPromoCode,
    formData,
    setIncludeInactive,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleToggleActive,
  };
}
